if (!customElements.get('home-featured-carousel')) {
  customElements.define('home-featured-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.home-featured__track');
      this.slides = [...this.querySelectorAll('.home-featured__slide')];
      this.buttons = [...this.querySelectorAll('[data-slide]')];
      if (!this.track || this.slides.length < 2) return;
      this.index = 0;
      this.autoplayDelay = Number(this.dataset.autoplay) || 0;
      this.userPaused = false;
      this.hovering = false;
      this.interacting = false;
      this.inView = true;
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayButton = this.querySelector('[data-autoplay-toggle]');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.querySelectorAll('.home-featured__selectors, .home-featured__arrows').forEach(control => { control.hidden = false; });
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-autoplay-toggle')) {
          this.userPaused = !this.userPaused;
          this.resumeRequested = !this.userPaused;
          this.updateAutoplayButton();
          this.scheduleAutoplay();
          return;
        }
        this.resumeRequested = false;
        if (button.hasAttribute('data-slide')) this.goTo(Number(button.dataset.slide));
        if (button.hasAttribute('data-direction')) this.goTo(this.index + Number(button.dataset.direction));
      }, options);
      this.track.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
          this.buttons.forEach((button, i) => {
            if (i === this.index) button.setAttribute('aria-current', 'true');
            else button.removeAttribute('aria-current');
          });
        });
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        this.goTo(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const i = this.slides.findIndex(slide => slide.id === `HomeFeaturedSlide-${event.detail.blockId}`);
        if (i >= 0) this.goTo(i, false);
      }, options);
      if (this.autoplayDelay > 0) {
        this.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'mouse') return;
          this.resumeRequested = false;
          this.hovering = true;
          this.stopAutoplay();
        }, options);
        this.addEventListener('pointerleave', event => {
          if (event.pointerType !== 'mouse') return;
          this.hovering = false;
          this.scheduleAutoplay();
        }, options);
        this.addEventListener('focusin', () => {
          this.resumeRequested = false;
          this.stopAutoplay();
        }, options);
        this.addEventListener('focusout', () => queueMicrotask(() => this.scheduleAutoplay()), options);
        this.track.addEventListener('pointerdown', () => {
          this.interacting = true;
          this.stopAutoplay();
        }, { ...options, passive: true });
        const releasePointer = () => {
          if (!this.interacting) return;
          this.interacting = false;
          this.scheduleAutoplay();
        };
        document.addEventListener('pointerup', releasePointer, options);
        document.addEventListener('pointercancel', releasePointer, options);
        document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), options);
        this.motionQuery.addEventListener('change', () => {
          this.updateAutoplayButton();
          this.scheduleAutoplay();
        }, options);
        if ('IntersectionObserver' in window) {
          this.inView = false;
          this.visibilityObserver = new IntersectionObserver(entries => {
            this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.1;
            this.scheduleAutoplay();
          }, { threshold: 0.1 });
          this.visibilityObserver.observe(this);
        }
        this.updateAutoplayButton();
        this.scheduleAutoplay();
      }
    }
    updateAutoplayButton() {
      if (!this.autoplayButton) return;
      this.autoplayButton.hidden = this.motionQuery.matches;
      this.autoplayButton.setAttribute('aria-label', this.userPaused ? 'Začni samodejno menjavanje pasic' : 'Ustavi samodejno menjavanje pasic');
      this.autoplayButton.querySelector('[data-pause-icon]').toggleAttribute('hidden', this.userPaused);
      this.autoplayButton.querySelector('[data-play-icon]').toggleAttribute('hidden', !this.userPaused);
    }
    stopAutoplay() {
      clearTimeout(this.autoplayTimer);
    }
    scheduleAutoplay() {
      this.stopAutoplay();
      if (!this.isConnected || !this.autoplayDelay || this.userPaused || this.interacting || !this.inView || document.hidden || this.motionQuery.matches) return;
      if (!this.resumeRequested && (this.hovering || this.contains(document.activeElement))) return;
      this.autoplayTimer = setTimeout(() => this.goTo(this.index + 1), this.autoplayDelay);
    }
    goTo(index, smooth = true) {
      const target = (index + this.slides.length) % this.slides.length;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: smooth && !reduced ? 'smooth' : 'instant' });
      this.scheduleAutoplay();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.stopAutoplay();
      this.visibilityObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  });
}
